import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { AuthService } from 'app/core/auth/auth.service';
import { PlatformService } from 'app/core/platform/platform.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Artist, Genre, ParticipantRole, Release, Track, TrackParticipant } from 'app/modules/user/releases/releases.types';
import { AudioPlayerService } from 'app/shared/components/audio-player/audio-player.service';
import { DurationPipe } from 'app/shared/shared.directives';
import { Subject, takeUntil, timer } from 'rxjs';

@Component({
    selector: 'app-release-summary',
    templateUrl: './release-summary.component.html',
    animations: fuseAnimations,
    styleUrls: ['release-summary.component.scss'],
    exportAs: 'releaseSummary'
})

export class ReleaseSummaryComponent implements OnInit, OnChanges, OnDestroy {
    @Input() release: Release;
    @Output() closeEmitter: EventEmitter<void> = new EventEmitter<void>();

    playingTrack: Track;
    thisUser: User;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        public platformService: PlatformService,
        public router: Router,
        public activatedRoute: ActivatedRoute,
        private _ngZone: NgZone,
        private _changeDetectorRef: ChangeDetectorRef,
        private _authService: AuthService,
        private durationPipe: DurationPipe,
        private userService: UserService,
        private audioPlayerService: AudioPlayerService,
    ) {
    }
    ngOnChanges(changes: SimpleChanges): void {

    }

    ngOnInit(): void {
        this.userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe(user => {
            this.thisUser = user;
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    goToAnalytics() {
        let route = '/analytics';
        if (this.thisUser?.role === 'partner') {
            route = '/partner/analytics';
        } else if (this.thisUser?.role === 'admin') {
            route = '/admin/analytics';
        }
        this.router.navigate([route], { queryParams: { id: this.release.uuid } });
        this.closeEmitter.emit();
    }

    getGenreNames(primaryGenre: Genre, subGenre?: Genre): string {
        let genres: Genre[] = [];
        if (primaryGenre) {
            genres.push(primaryGenre);
        }

        if (subGenre) {
            genres.push(subGenre);
        }
        return genres.map(g => g.title).join(', ');
    }

    getArtists(release: Release): Artist[] {
        return release?.participants?.filter(p => p?.role === ParticipantRole.MainArtist).map(p => p?.participant);
    }

    getFeatArtists(release: Release): Artist[] {
        return release?.participants?.filter(p => p?.role === ParticipantRole.FeaturedArtist).map(p => p?.participant);
    }

    getArtistNames(artists: Artist[]): string {
        return artists?.map(participant => participant?.title).join(', ');
    }

    getTrackArtistNames(artists: TrackParticipant[]): string {
        let mainArtists = artists?.filter(participant => participant.role === ParticipantRole.MainArtist).sort((a, b) => a.sortOrder - b.sortOrder).map(participant => participant.participant?.title);
        let featuredArtists = artists?.filter(participant => participant.role === ParticipantRole.FeaturedArtist).sort((a, b) => a.sortOrder - b.sortOrder).map(participant => participant.participant?.title);
        return mainArtists?.join(', ') + (featuredArtists?.length ? ' (feat. ' + featuredArtists?.join(', ') + ')' : '');
    }

    getOtherTrackParticipants(artists: TrackParticipant[]): string {
        return artists?.filter(participant => participant.participant && participant.role !== ParticipantRole.MainArtist && participant.role !== ParticipantRole.FeaturedArtist)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(participant => participant.participant?.title)
            .join(', ');
    }

    playTrack(event: MouseEvent, track: Track) {
        event?.stopPropagation();
        event?.preventDefault();

        this.audioPlayerService.setRelease(this.release);
        this.audioPlayerService.playTrack(track);
    }
}
